import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'

import { debounce, groupBy, difference, union } from 'lodash'
import { ChevronDown, ChevronUp, Search } from 'lucide-react'
import { DefaultSheet, Separator, SheetBody, SheetFooter, Button, Col, Row, Input, Checkbox, Label, Collapsible, CollapsibleTrigger, CollapsibleContent } from 'twyne-new.ui'

const propTypes = {
  fields: PropTypes.array,
  columns: PropTypes.object,
  onApply: PropTypes.func,
  onCancel: PropTypes.func,
}

export default class Configure extends React.Component {
  state = {
    selectedFieds: {},
    filteredFields: [],
    allFields: [],
    searchTerm: '',
  }

  componentDidMount() {
    const { fields, allFields } = this.props
    const selectedFieds = {}
    fields.forEach(field => selectedFieds[field] = true)
    this.setState({ selectedFieds, allFields, filteredFields: allFields })
  }

  handleApply = () => {
    const { selectedFieds } = this.state
    const fields = Object.keys(selectedFieds).filter(key => selectedFieds[key])
    this.props.onApply(fields)
  }

  handleChangeCheckbox = (checked, key) => {
    const { selectedFieds } = this.state
    selectedFieds[key] = checked
    this.setState({ selectedFieds })
  }

  setFilters = () => {
    const { searchTerm, allFields } = this.state
    const regex = RegExp(searchTerm, 'i')

    const filteredFields = allFields.filter(field => {
      if (regex.test(field.label) || regex.test(field.value)) {
        return field
      }
    })

    this.setState({ filteredFields })
  }

  applyFilters = () => debounce(this.setFilters, 400)()

  handleChangeSearchTerm = value => {
    this.setState({ searchTerm: value })
    this.applyFilters()
  }

  Field = ({ label, dataField }) => {
    const value = this.state.selectedFieds[dataField]
    const key = `${dataField}-${value ? 1 : 0}`

    return <Col size="4" key={key} className='flex items-center gap-2 cursor-pointer' onClick={() => this.handleChangeCheckbox(!value, dataField)}>
      <Checkbox value={value} checked={value} />
      <Label>
        {label}
      </Label>
    </Col>
  }

  fieldsPicker = (groupFields, selectedFields, action = "Check") => {
    switch (action) {
      case "Check":
        return difference(groupFields, selectedFields).length
      case "Select":
        return union(groupFields, selectedFields)
      case "Unselect":
        return difference(selectedFields, groupFields)
    }
  }

  Group = ({ name, fields }) => {
    const selectedDataFields = Object.keys(this.state.selectedFieds)
    const dataFields = fields.map(({ value }) => value)
    const [isOpen, setIsOpen] = useState(true)
    const [action, setAction] = useState("Select")

    useEffect(() => {
      setAction(handleFieldsPicker("Check") ? 'Select' : 'Unselect')
    }, [selectedDataFields])

    const handleFieldsPicker = action => this.fieldsPicker(dataFields, selectedDataFields, action)

    const handleClick = event => {
      event.stopPropagation()
      const selectedFieds = {}
      handleFieldsPicker(action).forEach(field => selectedFieds[field] = true)
      this.setState({ selectedFieds })
    }

    return <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <Row className='pb-3'>
        <Col size='12'>
          <div className='flex items-center justify-between'>
            <span className='twy-text-base-accent text-md'>{name}</span>
            <div className='flex items-center gap-2'>
              <Button onClick={event => handleClick(event)} size='sm' color='accent' variant='ghost' className='lowercase'>
                {`${action} all`}
              </Button>
              <CollapsibleTrigger asChild>
                <Button variant='ghost' size='sm' className='flex justify-center'>
                  {isOpen ? <ChevronUp size='14' /> : <ChevronDown size='14' />}
                </Button>
              </CollapsibleTrigger>
            </div>
          </div>
        </Col>
        <Col>
          <CollapsibleContent className='flex flex-col gap-2'>
            <Row>
              {fields.map(field => {
                const { label, value: dataField } = field
                return <this.Field key={dataField} label={label} dataField={dataField} />
              })}
            </Row>
          </CollapsibleContent>
        </Col>
        <Col>
          <Separator />
        </Col>
      </Row>
    </Collapsible>
  }

  FieldsCard = () => {
    const { filteredFields } = this.state
    const groups = groupBy(filteredFields, 'group')

    if (!filteredFields || !filteredFields.length) {
      return <div className="text-center p-4 twy-text-base-accent">Field not found</div>
    }

    return Object.entries(groups).map(([group, fields]) => <this.Group key={group} name={group} fields={fields} />)
  }

  SearchCard = () => {
    const selectedDataFields = Object.keys(this.state.selectedFieds)
    const allDataFields = this.state.allFields.map(({ value }) => value)
    const [action, setAction] = useState("Select")

    useEffect(() => {
      setAction(handleFieldsPicker("Check") ? 'Select' : 'Unselect')
    }, [selectedDataFields])

    const handleFieldsPicker = action => this.fieldsPicker(allDataFields, selectedDataFields, action)

    const handleClick = event => {
      event.stopPropagation()
      const selectedFieds = {}
      handleFieldsPicker(action).forEach(field => selectedFieds[field] = true)
      this.setState({ selectedFieds })
    }

    return <>
      <div className='relative w-full flex items-center justify-between'>
        <Input
          placeholder='Search...'
          className='pl-8 border-0 focus:bg-gray-200 focus:dark:bg-gray-950! z-0'
          value={this.state.searchTerm}
          onChange={e => this.handleChangeSearchTerm(e.target.value)}
        />
        <Search size='18' className='absolute z-10 left-2 top-1/2 -translate-y-1/2' />
        <Button onClick={event => handleClick(event)} variant='light' color='accent'>
          {`${action} all`}
        </Button>
      </div>
    </>
  }

  render() {
    return <DefaultSheet
      contentProps={{ className: 'h-[85%] w-[60%] max-w-[1000px] flex flex-col overflow-hidden' }}
      title='Configure Fields'
      trigger={<>
        <Button variant='outline' size="lg">
          Configure
        </Button>
      </>}
      wrapperProps={{ defaultOpen: true }}
    >
      <SheetBody className='border-0! flex-1 min-h-0 overflow-y-auto overflow-x-hidden pt-2'>
        <this.SearchCard />
        <Separator className='my-3' />
        <this.FieldsCard />
      </SheetBody>
      <SheetFooter className='mt-auto flex justify-end border-t twy-border-base bg-white dark:bg-gray-1200'>
        <Button size='lg' onClick={this.props.onCancel} variant='ghost'>Cancel</Button>
        <Button size='lg' onClick={this.handleApply} color="accent">Apply</Button>
      </SheetFooter>
    </DefaultSheet>
  }
}

Configure.propTypes = propTypes
Configure.defaultProps = {}
